// BlogDetail.jsx
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { fetchBlogDetailServices } from "../api/BlogDetailApi";
import ErrorPage from "../not_found/NotFound";
import Loader from "../ui/Loader";
import { useTranslation } from "react-i18next";

const BlogDetail = () => {
  const { t } = useTranslation("not_found");
  const { slug } = useParams();
  const [blogDetail, setBlogDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const getBlogDetail = async () => {
      try {
        const response = await fetchBlogDetailServices(slug);
        // Ensure the content is an array (if not, convert it)
        const formattedContent = response.data.content
          ? Array.isArray(response.data.content)
            ? response.data.content
            : Object.values(response.data.content)
          : [];
        setBlogDetail({
          ...response.data,
          content: formattedContent,
        });
      } catch (err) {
        console.error("Error fetching blog detail:", err);
        setError(
          <ErrorPage
            title={t("blogs_details_not_found_title")}
            description={t("service_not_found_description")}
          />
        );
      } finally {
        setLoading(false);
      }
    };

    getBlogDetail();
  }, [slug, t]);

  if (loading) return <Loader />;
  if (error) return <div className="text-red-500">{error}</div>;
  if (!blogDetail) return <div>No data available</div>;

  return (
    <div className="relative">
      {/* Hero Section */}
      <div className="relative h-[384px] md:h-[384px] lg:h-[350px]">
        <img
          src="/assets/servicesbg.svg"
          className="w-full h-full object-cover"
          alt="background animation"
        />
        <div className="absolute inset-0 bg-[#00000099]"></div>
        <img
          src="/assets/ellipse.svg"
          className="absolute bottom-64 left-1/2 transform -translate-x-1/2 translate-y-1/2"
          alt="ellipse decoration"
        />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center px-2 md:px-4">
          <div className="title-small text-[#05897E] py-2 px-4 rounded-full mb-4 border border-[#FFFFFF33]">
          {t("blog")}
          </div>
          <div className="max-w-[1160px]">
            <h1 className="web-heading-main mb-4">{blogDetail.title}</h1>
            <p className="text-color body-large-30 mb-6 md:text-xl">
              {blogDetail.short_description}
            </p>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="relative text-white px-4 py-8 max-w-7xl mx-auto mb-10">
        <div className="flex flex-col md:flex-row items-center justify-center">
          <div className="w-full space-y-6">
            {/* Heading 1 Section */}
            {blogDetail.content.map((section, index) => {
              if (section.heading1) {
                return (
                  <div key={`heading1-${index}`}>
                    <h2 className="web-heading-main-4 text-color-6 tracking-tight mb-4">
                      {section.heading1}
                    </h2>
                    {Array.isArray(section.description1) ? (
                      section.description1.map((desc, i) => (
                        <p key={i} className="mb-6 body-large-9 text-color-7 tracking-[1px]">{desc}</p>
                      ))
                    ) : (
                      <p className="mb-6 body-large-9 text-color-7 tracking-[1px]">{section.description1}</p>
                    )}
                    {section.features1 && section.features1.length > 0 && (
                      <ul className="list-disc pl-6 body-large-8 text-color-7 tracking-[1px]">
                        {section.features1.map((feature, i) => (
                          <li key={i}>{feature}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              }
              return null;
            })}

            {/* Heading 2 Section */}
            {blogDetail.content.map((section, index) => {
              if (section.heading2) {
                return (
                  <div key={`heading2-${index}`}>
                    <h2 className="web-heading-main-4 text-color-6 tracking-tight mb-4">
                      {section.heading2}
                    </h2>
                    {Array.isArray(section.description2) ? (
                      section.description2.map((desc, i) => (
                        <p key={i} className="mb-6 body-large-9 text-color-7 tracking-[1px]">{desc}</p>
                      ))
                    ) : (
                      <p className="mb-6 body-large-9 text-color-7 tracking-[1px]">{section.description2}</p>
                    )}
                    {section.features2 && section.features2.length > 0 && (
                      <ul className="list-disc pl-6 body-large-8 text-color-7 tracking-[1px]">
                        {section.features2.map((feature, i) => (
                          <li key={i}>{feature}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              }
              return null;
            })}

            {/* Heading 3 Section with Image */}
            {(() => {
              // Get the third content element (index 2, zero-based)
              const item = blogDetail.content[2] || {};

              // Check if `description3` or `features3` is empty (either string or array)
              const isFlexCol =
                (Array.isArray(item.description3) && item.description3.length === 0) ||
                item.description3 === "" ||
                (Array.isArray(item.features3) && item.features3.length === 0) ||
                item.features3 === "";

              // Apply layout conditionally
              const containerClass = isFlexCol ? "flex-col" : "md:flex flex-col md:items-center md:space-y-0";

              return (
                <div className={containerClass}>
                  {blogDetail.content.some((section) => section.heading3) ? (
                    blogDetail.content.map((section, index) =>
                      section.heading3 ? (
                        <div key={`heading3-${index}`} className="w-full md:w-1/2">
                          <h2 className="web-heading-main-4 text-color-6 tracking-tight mb-4">
                            {section.heading3}
                          </h2>
                          {Array.isArray(section.description3) ? (
                      section.description3.map((desc, i) => (
                        <p key={i} className="mb-6 body-large-9 text-color-7 tracking-[1px]">{desc}</p>
                      ))
                    ) : (
                      <p className="mb-6 body-large-9 text-color-7 tracking-[1px]">{section.description3}</p>
                    )}
                          {section.features3 && section.features3.length > 0 && (
                            <ul className="list-disc pl-6 body-large-8 text-color-7 tracking-[1px]">
                              {section.features3.map((feature, i) => (
                                <li key={i}>{feature}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ) : null
                    )
                  ) : (
                    <div className="md:w-1/2"></div>
                  )}

                  {/* Image Section (Always Rendered) */}
                  <div className="w-full md:w-1/2 flex justify-center mt-8 md:mt-0 relative">
                    {blogDetail.image && blogDetail.image.length > 0 && (
                      <>
                        <img
                          src={blogDetail.image[0]}
                          className="max-w-full max-h-auto"
                          alt="Blog Image"
                        />
                        <img
                          src="/assets/ellipse.svg"
                          className="absolute inset-0 bottom-8 left-1/2 transform -translate-x-1/2"
                          alt="ellipse decoration"
                        />
                      </>
                    )}
                  </div>
                </div>
              );
            })()}


            {/* Heading 4 Section */}
            {blogDetail.content.map((section, index) => {
              if (section.heading4) {
                return (
                  <div key={`heading4-${index}`}>
                    <div className="md:w-1/2 space-y-6">
                      <h2 className="web-heading-main-4 text-color-6 tracking-tight mb-4">
                        {section.heading4}
                      </h2>
                      {Array.isArray(section.description4) ? (
                      section.description4.map((desc, i) => (
                        <p key={i} className="mb-6 body-large-9 text-color-7 tracking-[1px]">{desc}</p>
                      ))
                    ) : (
                      <p className="mb-6 body-large-9 text-color-7 tracking-[1px]">{section.description4}</p>
                    )}
                    </div>
                    {section.features4 && section.features4.length > 0 && (
                      <ul className="list-disc list-inside body-large-8 text-color-7 tracking-[1px]">
                        {section.features4.map((feature, i) => (
                          <li key={i}>{feature}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              }
              return null;
            })}

            {/* Heading 5 Section */}
            {blogDetail.content.map((section, index) => {
              if (section.heading5) {
                return (
                  <div key={`heading5-${index}`} className="md:w-1/2 mt-[-229px]">
                    <div className="pt-3 text-color-7">
                      <h2 className="web-heading-main-5 text-color-6 tracking-tight mb-5">
                        {section.heading5}
                      </h2>
                      {Array.isArray(section.description5) ? (
                      section.description5.map((desc, i) => (
                        <p key={i} className="mb-6 body-large-9 text-color-7 tracking-[1px]">{desc}</p>
                      ))
                    ) : (
                      <p className="mb-6 body-large-9 text-color-7 tracking-[1px]">{section.description5}</p>
                    )}
                    </div>
                    {section.features5 && section.features5.length > 0 && (
                      <ul className="list-disc body-large-8 pl-6 text-color-7 tracking-[1px]">
                        {section.features5.map((feature, i) => (
                          <li key={i}>{feature}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              }
              return null;
            })}

            {/* Heading 6 Section */}
            {blogDetail.content.map((section, index) => {
              if (section.heading6) {
                return (
                  <div key={`heading6-${index}`} className="md:flex md:items-center md:w-1/2 mt-[-150px]">
                    <div>
                    {Array.isArray(section.description6) ? (
                      section.description6.map((desc, i) => (
                        <p key={i} className="mb-6 body-large-9 text-color-7 tracking-[1px]">{desc}</p>
                      ))
                    ) : (
                      <p className="mb-6 body-large-9 text-color-7 tracking-[1px]">{section.description6}</p>
                    )}
                    </div>
                    {section.features6 && section.features6.length > 0 && (
                      <ul className="list-disc list-inside pl-6 body-large-9 text-color-7 tracking-[1px]">
                        {section.features6.map((feature, i) => (
                          <li key={i}>{feature}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              }
              return null;
            })}
               {/* Dynamic Section 6 onwards*/}

               {blogDetail.content.map((section, index) => {
              return Object.keys(section).map((key) => {
                const match = key.match(/^heading(\d+)$/);
                if (match) {
                  const number = parseInt(match[1], 10); // Extract the number from "headingX"
                  if (number > 5) { // Only process headings after heading6
                    return (
                      <div key={`${index}-${number}`} className="md:flex flex-col md:items-start md:w-1/2 mt-[-150px]">
                        <div>
                          <h2 className="web-heading-main-5 text-color-6 tracking-tight mb-2">
                            {section[`heading${number}`]}
                          </h2>
                          {Array.isArray(section[`description${number}`]) ? (
                            section[`description${number}`].map((desc, i) => (
                              <p key={i} className="mb-6 body-large-9 text-color-7 tracking-[1px]">{desc}</p>
                            ))
                          ) : (
                            <p className="mb-6 body-large-9 text-color-7 tracking-[1px]">{section[`description${number}`]}</p>
                          )}
                        </div>

                        {section[`features${number}`] && section[`features${number}`].length > 0 && (
                          <ul className="list-disc list-inside pl-6 body-large-9 text-color-7 tracking-[1px]">
                            {section[`features${number}`].map((feature, i) => (
                              <li key={i}>{feature}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  }
                }
                return null;
              });
            })}

          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogDetail;
